import { defineCopy } from '@/copy';

/** gamelab strings. Keep the voice: short, funny, no em dashes. */
export const copy = defineCopy('gamelab', {
  tile: 'Games your agent made up. Rules included.',
  common: {
    back: 'Back',
    loading: 'Loading the lab',
    failed: 'The lab hiccuped. Try again.',
    notFound: 'No such game. The agent may have eaten it.',
    players: '{min} to {max} players',
    playersOne: '{n} player',
    playersExact: '{n} players',
    minutes: '{n} min',
    hoop: 'Hoop',
    noHoop: 'No hoop',
    offline: 'No signal. This will wait.',
  },
  lab: {
    title: 'Game Lab',
    line: 'Games your agent invents. You get the rules and a play screen.',
    ask: 'Ask the agent for a game',
    paste: 'Paste a draft',
    fresh: 'Fresh from the agent',
    freshEmpty: 'Nothing new. The agent is thinking very hard.',
    shelf: 'The shelf',
    shelfEmpty: 'The shelf is empty. Odd. Reload and the classics come back.',
    workbench: 'Workbench',
    workbenchEmpty: "The agent's bench is empty. Ask for something weird.",
    workbenchEdited: 'Claude edited this {ago}',
    retired: 'Retired',
    newSticker: 'NEW',
    draftSticker: 'DRAFT',
    liveRuns: 'Being played now',
    resume: 'Resume',
    watch: 'Watch',
    requests: 'Requests',
    requestsOpen: 'Requests ({n})',
    agentOnly: 'Connect your agent',
    ago: {
      now: 'just now',
      min: '{n} min ago',
      hour: '{n} h ago',
      day: '{n} d ago',
    },
  },
  ask: {
    title: 'Ask for a game',
    line: 'Tell the agent what you need. It reads this next time it opens the Lab.',
    placeholder: '3 players, one ball, no hoop, maximum chaos',
    textLabel: 'What should it make?',
    playersMin: 'At least',
    playersMax: 'At most',
    hoop: 'We have a hoop',
    hoopHelp: 'Off means the agent should not need one',
    hoopAny: "Don't care",
    equipment: 'Stuff we have',
    send: 'Send it',
    sent: 'Sent. The agent has homework.',
    tooMany: 'Five requests a day. The agent needs a snack break.',
    empty: 'No requests yet. The agent is just sitting there. Give it homework.',
    open: 'Open',
    answered: 'Answered',
    closed: 'Closed',
    thread: 'Thread',
    replyLabel: 'Add a note',
    replyPlaceholder: 'Say more, or say thanks',
    reply: 'Post note',
    close: 'Close request',
    fromAgent: 'Agent',
    fromYou: 'You',
    draftAttached: 'A game came with it',
    openDraft: 'Open the game',
    noReplies: 'No answers yet.',
    asked: 'Asked by {name}',
    equipmentNames: {
      ball: 'Ball',
      second_ball: 'Second ball',
      cones: 'Cones',
      chairs: 'Chairs',
      stopwatch: 'Stopwatch',
      chalk: 'Chalk',
    },
  },
  game: {
    madeBy: 'Made by the agent',
    madeFor: 'Made by the agent for a crew request',
    builtin: 'A classic, built in',
    dryRun: 'Dry run',
    play: 'Play this',
    shelve: 'Put it on the shelf',
    shelveAsk: 'Shelve {name} for everyone?',
    shelveBody: 'It moves to the shelf and the whole crew can play it.',
    shelveConfirm: 'Shelve it',
    shelved: 'On the shelf.',
    notReady: 'This draft needs a clean check and a passing test run first.',
    sendBack: 'Send it back',
    sendBackTitle: 'What should the agent fix?',
    sendBackPlaceholder: 'Too slow with 8 players. The bonus never triggers.',
    sendBackSend: 'Send note',
    sentBack: 'The agent will read it next time.',
    retire: 'Retire',
    retireAsk: 'Retire {name}?',
    retireBody: 'It leaves the shelf. Old results stay.',
    retireConfirm: 'Retire it',
    retired: 'Retired.',
    discard: 'Discard draft',
    discardAsk: 'Throw this draft away?',
    discardConfirm: 'Discard',
    discarded: 'Gone.',
    edit: 'Edit the JSON',
    rules: 'Rules',
    options: 'Options',
    versions: 'Versions',
    versionLine: 'Version {n}',
    pitch: 'The pitch',
    statusLabels: {
      draft: 'Draft',
      ready: 'Ready',
      shelf: 'On the shelf',
      superseded: 'Replaced',
      retired: 'Retired',
      discarded: 'Discarded',
    },
    stats: '{runs} runs, {finals} finished',
    noStats: 'Nobody has played it yet.',
    checks: 'Checks',
    checksOk: 'Clean. No errors.',
    checksTested: 'Test run passed.',
    checksNotTested: 'No test run yet.',
    errors: 'Problems',
    warnings: 'Heads up',
    thumbs: 'How was it?',
    levels: ['Easy', 'Medium', 'Spicy'],
  },
  rulesKind: {
    setup: 'Setup',
    play: 'How it goes',
    scoring: 'Scoring',
    win: 'How you win',
    tips: 'Tips',
  },
  draft: {
    title: 'Paste a draft',
    editTitle: 'Edit the draft',
    line: 'The agent cannot write here? Paste its game as JSON. Same checks, same test run.',
    label: 'Game JSON',
    placeholder: '{ "dsl": 1, "meta": { ... } }',
    check: 'Check it',
    checking: 'Checking',
    clean: 'Clean. {actions} actions, {phases} phases.',
    problems: '{n} problems',
    tryIt: 'Try it',
    save: 'Save as draft',
    saved: 'Saved to the workbench.',
    tooBig: 'That is over 64 KB. Trim it.',
    notJson: "That isn't valid JSON yet.",
    empty: 'Paste something first.',
    forRequest: 'This answers a request',
    format: 'Ask the agent for the format',
    formatHelp: 'Copy this prompt into a chat with your agent.',
    promptTitle: 'Prompt for a read-only agent',
    prompt:
      'Write a basketball pickup game for our crew as one JSON document in the Game Lab format (dsl 1). Request: {request}. Read the format first if you can. Reply with only the JSON, no commentary.',
    savedDry: 'Saved. Now give it a dry run.',
    slugTaken: 'That slug is taken. Change meta.slug.',
    pathHint: 'at {path}',
  },
  dry: {
    title: 'Dry run',
    banner: "Dry run. Nothing counts, nobody's ego gets hurt.",
    players: 'Fake players',
    start: 'Start dry run',
    again: 'Reset',
    exit: 'Leave',
    pickCrew: 'Or pick real names',
    done: 'The game says it is over. Nice.',
    stuck: 'Nothing can be pressed. The game may be stuck.',
    steps: '{n} plays',
  },
  runNew: {
    title: 'Set up the run',
    line: 'Pick who plays. This phone keeps the book.',
    players: 'Who plays',
    needMore: 'Need at least {min}.',
    tooMany: 'This game takes {max} at most.',
    options: 'Options',
    teams: 'Teams',
    teamsHelp: 'Balanced by rating',
    shuffle: 'Shuffle the order',
    session: 'Part of {name}',
    detach: 'Not part of the run',
    tipOff: 'Tip off',
    starting: 'Starting',
    failed: 'Could not start. Try again.',
    optionOn: 'On',
    optionOff: 'Off',
    unfair: 'Order: {names}',
  },
  run: {
    live: 'Live',
    final: 'Final',
    keeping: 'You keep the book',
    someoneKeeps: '{name} keeps the book',
    take: 'Take the book',
    takeAsk: '{name} has the book. Take it?',
    takeConfirm: 'Take it',
    taken: 'You have the book.',
    lost: "These plays didn't count, someone else had the book.",
    rejected: "That play doesn't fit right now.",
    undo: 'Undo: {what}',
    undoNothing: 'Nothing to undo',
    undoDice: "Undo won't save you from the dice.",
    undone: 'Undone.',
    finalQ: 'Final?',
    finalBody: 'The game says it is over. Lock the result?',
    finalConfirm: 'Lock it',
    finalNot: 'Keep playing',
    finishEarly: 'End now',
    finishAsk: 'End the run?',
    finishBody: 'The result locks with the scores as they are.',
    abandon: 'Abandon run',
    abandonAsk: 'Abandon this run?',
    abandonBody: 'It will not count for anyone.',
    abandoned: 'This run was abandoned.',
    waiting: '{n} plays waiting for signal',
    waitingOne: '1 play waiting for signal',
    wakeOff: 'Screen may sleep',
    watching: 'Watching live',
    watchLine: '{name} keeps the book',
    rules: 'Rules',
    pickWho: 'Who did it?',
    pickSpot: 'Which spot?',
    pickValue: 'How many?',
    pickChoice: 'Pick one',
    confirmTitle: 'Sure?',
    notFound: 'No such run. It may have wandered off the court.',
    connecting: 'Connecting',
    reconnecting: 'Reconnecting',
    timeLeft: '{s}s',
    timesUp: "Time's up",
    start: 'Start',
    stop: 'Stop',
    roll: 'Roll',
    ticking: 'Clock running',
    dice: 'Dice',
    spinner: 'Spinner',
    round: 'Round {n}',
    roundOf: 'Round {n} of {total}',
    turn: 'Turn',
    alive: '{n} alive',
    out: 'Out',
    queue: 'Waiting',
    holder: 'Holds',
    challenger: 'Challenger',
    scoreboard: 'Scoreboard',
    spectatorNote: 'Spectators see the screen but cannot press anything.',
    lastPlay: 'Last play',
  },
  result: {
    title: 'Result',
    winner: 'Winner',
    winners: 'Winners',
    place: 'Place {n}',
    points: '{n} pts',
    xp: '+{n} Fun XP',
    xpNone: 'No Fun XP. Too short or too quiet to count.',
    xpCapped: 'Daily Fun XP cap reached.',
    funTag: 'The tag',
    runBack: 'Run it back',
    rate: 'Rate it',
    rated: 'Thanks.',
    up: 'Good',
    down: 'Meh',
    home: 'Back to the Lab',
    abandoned: 'Abandoned',
    abandonedBody: 'This run was called off. Nothing counts.',
    syncing: 'Result shows up when you get signal.',
    sameCrew: 'Same players, new seed.',
    badge: 'New badge: {name}',
  },
  badges: {
    lab_rat: 'Lab Rat',
    mad_scientist: 'Mad Scientist',
    lab_rat_line: 'Finished 5 different Lab games.',
    mad_scientist_line: 'Asked for a game that made the shelf.',
  },
  labStrip: {
    title: 'Lab',
    line: 'No Lab games played. Science is patient.',
    xp: '{n} Fun XP',
    today: '{n} of {cap} today',
    recent: 'Recent',
    won: 'Won',
    placed: 'Place {n}',
  },
  h2h: {
    line: 'Lab games: {a}-{b}',
    none: 'No Lab duels yet',
  },
  recap: {
    one: '1 Lab game: {names}.',
    many: '{n} Lab games: {names}.',
  },
  fresh: {
    title: 'New from the agent',
    line: '{name} is ready for a look.',
    many: '{n} new games are waiting for a look.',
    open: 'Take a look',
  },
  admin: {
    title: 'Agent',
    pageLine: 'Your agent invents games. The crew shelves and plays them.',
    sectionLine: 'Connect Claude or ChatGPT, mint a token, see what the agent touched.',
    open: 'Open agent settings',
    off: 'The connector is switched off on this server.',
    connections: 'Connections',
    connectionsEmpty: 'Nothing connected yet.',
    kindStatic: 'Token',
    kindOauth: 'Connected app',
    lastUsed: 'Last used {ago}',
    neverUsed: 'Never used',
    revoke: 'Revoke',
    revokeAsk: 'Revoke {name}?',
    revokeBody: 'It stops working right now.',
    revokeConfirm: 'Revoke',
    revoked: 'Revoked',
    scopes: 'Can: {list}',
    scopeNames: {
      'lab:read': 'read the shelf',
      'lab:write': 'write drafts',
      'lab:submit': 'offer games',
    },
    connect: 'Connect an agent',
    getCode: 'Get a code',
    codeLine: 'Type this on the page that opens. It works once, for 10 minutes.',
    codeExpires: 'Expires in {m} min',
    codeCopy: 'Copy code',
    codeCopied: 'Copied.',
    mint: 'Mint a token',
    mintLine: 'For Claude Code and anything that can send a header.',
    mintName: 'Name',
    mintNamePlaceholder: 'Claude Code on my laptop',
    mintDays: 'Lasts',
    mintDaysOptions: { 30: '30 days', 90: '90 days', 365: '1 year' },
    mintDo: 'Mint it',
    tokenOnce: 'Shown once. Copy it now.',
    tokenCopy: 'Copy token',
    tokenCommand: 'Claude Code command',
    commandCopy: 'Copy command',
    done: 'Done',
    settings: 'Privacy and court',
    hideNames: 'Hide names from the agent',
    hideNamesHelp: 'On: players show as P1, P2 and so on. Off: first names or nicknames.',
    courtTitle: 'Your court',
    hoops: 'Hoops',
    balls: 'Balls',
    cones: 'Cones',
    notes: 'Anything odd about the court',
    notesPlaceholder: 'Cracked three-point line, one rim is lower',
    saveSettings: 'Save',
    savedSettings: 'Saved.',
    enabled: 'Lab on',
    enabledHelp: 'Off pauses the agent tools.',
    audit: 'What the agent did',
    auditEmpty: 'Quiet so far.',
    auditMore: 'Show more',
    auditRow: '{tool} {ok}',
    auditOk: 'ok',
    auditFail: 'failed: {code}',
    promptTitle: 'Prompt for your phone',
    promptCopy: 'Copy prompt',
    prompt: 'Check the TTS Basketball requests and make us a game for tonight.',
    promptHelp: 'Paste it into a chat with the connector switched on.',
  },
  guide: {
    title: 'Connect your agent',
    claudeTitle: 'Claude (tested)',
    claudeIntro:
      'Claude is the path we tested. Setup takes a few minutes on a computer, then it works from your phone.',
    claudeSteps: [
      'On claude.ai or in Claude Desktop, open Customize, then Connectors.',
      'Tap + Add, then Add custom connector.',
      'Name it TTS Lab and paste the address below. Tap Continue.',
      "For the sign-in choose Use Claude's published identity if you see it, or Register automatically.",
      'A page from this app opens. Tap Get a code here, type it there, tap Let it in.',
      'In any chat, tap +, then Connectors, and switch TTS Lab on.',
    ],
    mobileNote:
      'Not checked: whether the Claude phone app can add a custom connector itself. Add it once on the web or desktop and it shows up on your phone.',
    freeNote: 'A free Claude account gets one custom connector, and this one takes the slot.',
    address: 'Connector address',
    addressCopy: 'Copy address',
    codeCopyHint: 'The code comes from the button above.',
    codeToolsTitle: 'Claude Code',
    codeToolsBody: 'Mint a token above and run the command it shows.',
    chatgptTitle: 'ChatGPT (depends on your plan)',
    chatgptBody:
      'ChatGPT needs a remote connector with sign-in, set up on the web. Developer mode and write access have been limited to Business, Enterprise and Edu workspaces, and OpenAI has moved these menus around, so look in Settings for Apps or Connectors and Developer mode. If your plan only reads, use the request inbox: the crew posts a request, you paste the game it suggests with Paste a draft.',
    unverifiedTitle: 'What we could not check',
    unverified: [
      'Whether the Claude phone app can add a connector on its own.',
      'The exact ChatGPT menu names and which plans can write, because OpenAI changes them and its help pages would not load for us.',
      'This was written 4 Nov 2026. Menus drift, so trust your screen over this page.',
    ],
    publicNote: 'This address must be reachable from the internet. No VPN, no password wall.',
  },
});
